"use client";

import { useInView } from "motion/react";
import { useEffect, useRef, useState } from "react";

/**
 * Animates a leading-integer string from 0 → target the first time it scrolls
 * into view. Preserves any non-digit prefix/suffix (e.g. "50+", "100%", "À partir de 1290").
 * Respects prefers-reduced-motion (renders the final value instantly).
 */
export function CountUp({
  value,
  duration = 1.2,
  className,
}: {
  value: string;
  duration?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState<string>(() => initialDisplay(value));

  useEffect(() => {
    if (!inView) return;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Skip animation for ranges (7-14, 7–14) — looks weird counting one bound only.
    const isRange = /\d[\s]*[-–—][\s]*\d/.test(value);
    const match = value.match(/^(\D*)(\d+)([\s\S]*)$/);
    if (!match || isRange) {
      setDisplay(value);
      return;
    }
    const [, prefix, digits, suffix] = match;
    const target = parseInt(digits, 10);

    if (reduced || target === 0) {
      setDisplay(value);
      return;
    }

    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.floor(target * eased);
      setDisplay(`${prefix}${current}${suffix}`);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setDisplay(value);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className={className}>
      {/* Final value exposed to assistive tech via sr-only text — the
          visible counter changes every frame and is hidden from a11y to
          avoid screen-reader spam. axe-core also flags `aria-label` on
          plain spans, so this sibling pattern is the safe form. */}
      <span className="sr-only">{value}</span>
      <span aria-hidden>{display}</span>
    </span>
  );
}

function initialDisplay(value: string): string {
  const match = value.match(/^(\D*)(\d+)([\s\S]*)$/);
  if (!match) return value;
  const [, prefix, , suffix] = match;
  return `${prefix}0${suffix}`;
}
